import { useTheme } from '@emotion/react';
import {
  array,
  bool,
  func,
  string,
  oneOfType,
  number,
  shape,
} from 'prop-types';
import ReactSelect from 'react-select';
import Errors from './Errors';
import DisplayLine from './DisplayLine';
import { findTranslation } from '../../utils/helpers';
import WithLabel from './WithLabel';
import {useTranslation} from "react-i18next";

const customStyles = ({ theme: { fontWeights, colors }, styles, errors, disabled }) => ({
  control: (base) => ({
    ...base,
    boxShadow: 'none',
    fontWeight: fontWeights.bold,
    border: `1px solid ${errors ? colors?.alert : colors?.border}`,
    backgroundColor: disabled ? colors?.border : null,
    color: colors?.primaryFont,
    '&:hover': {
      borderColor: undefined,
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontWeight: fontWeights.thin,
    color: colors.secondaryFont,
  }),
  singleValue: (base) => ({
    ...base,
    color: colors?.primaryFont,
    fontWeight: disabled ? fontWeights.thin : null,
  }),
  dropdownIndicator: (base, { selectProps }) => ({
    ...base,
    transform: selectProps.menuIsOpen && 'rotate(180deg)',
  }),
  option: (base) => ({
    ...base,
    minHeight: '40px',
  }),
  ...styles,
});

const Select = ({
  errors,
  disabled,
  options,
  onChange,
  name,
  value,
  autoFocus,
  onFocus,
  styles,
  showTextErrors,
  emptyOption,
  clearKey: key,
  isSearchable,
  label,
  readOnly
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colors } = theme;
  const totalOptions = emptyOption && options.length > 0 ? [{ label: '', value: null }].concat(options) : options;

  if (readOnly) {
    return (
      <DisplayLine label={label} value={findTranslation(value, options)} />
    );
  }

  const input = (
    <>
      <ReactSelect
        key={key}
        autoFocus={autoFocus}
        isDisabled={disabled}
        name={name}
        width="200px"
        isSearchable={isSearchable}
        onChange={({ value }) => onChange({ target: { value, name } })}
        onFocus={onFocus}
        value={totalOptions.find((el) => el.value === value)}
        options={totalOptions}
        placeholder={disabled ? null : t('Selecteer een optie')}
        components={{ IndicatorSeparator: () => null }}
        styles={customStyles({ theme, errors, disabled, styles })}
        theme={(selectTheme) => ({
          ...selectTheme,
          colors: {
            ...selectTheme.colors,
            primary: colors.primary,
            primary75: colors.primaryActive,
            primary25: colors.primaryActive,
            primary50: colors.primaryBackground,
            danger: colors.alert,
            neutral0: colors.white,
            neutral20: colors.border,
          },
          spacing: {
            ...selectTheme.spacing,
            controlHeight: 40,
          },
        })}
      />
      {showTextErrors ? <Errors {...{ errors }} /> : null}
    </>
  );

  return label ? <WithLabel label={label}>{input}</WithLabel> : input;
};

Select.propTypes = {
  errors: oneOfType([bool, array]),
  disabled: bool,
  options: array.isRequired,
  onChange: func.isRequired,
  onFocus: func,
  name: string.isRequired,
  value: oneOfType([string, number, bool]),
  autoFocus: bool,
  styles: shape({}),
  showTextErrors: bool,
  isSearchable: bool,
};

Select.defaultProps = {
  showTextErrors: true,
  onFocus: () => {},
  isSearchable: false,
};

export default Select;
